// Driver-licence barcode (AAMVA PDF417) parsing for age checks. Run this on the register so the
// date of birth never has to leave the device; only pass/fail is sent to the server.

import { parseIso, type IsoDate } from './dates.js';

export interface LicenseData {
  dob: IsoDate | null;
  expiry: IsoDate | null;
  state: string | null;
}

function aamvaDate(v: string | undefined, country: 'US' | 'CA'): IsoDate | null {
  if (!v || !/^\d{8}$/.test(v)) return null;
  // US encodes MMDDCCYY, Canada CCYYMMDD.
  const iso = country === 'US' ? `${v.slice(4, 8)}-${v.slice(0, 2)}-${v.slice(2, 4)}` : `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}`;
  return Number.isNaN(Date.parse(iso)) ? null : iso;
}

export function parseAamva(raw: string): LicenseData {
  const fields: Record<string, string> = {};
  for (const line of raw.split(/\r\n|\n|\r|\x1e|\x1d/)) {
    const m = line.match(/(?:^|DL|ID)?(D[A-Z]{2})(.*)$/);
    if (m && m[1] && !(m[1] in fields)) fields[m[1]] = (m[2] ?? '').trim();
  }
  const country = fields['DCG'] === 'CAN' ? 'CA' : 'US';
  return {
    dob: aamvaDate(fields['DBB'], country),
    expiry: aamvaDate(fields['DBA'], country),
    state: fields['DAJ'] ?? null,
  };
}

export function ageOn(dob: IsoDate, today: IsoDate): number {
  const b = parseIso(dob);
  const t = parseIso(today);
  let age = t.getUTCFullYear() - b.getUTCFullYear();
  const m = t.getUTCMonth() - b.getUTCMonth();
  if (m < 0 || (m === 0 && t.getUTCDate() < b.getUTCDate())) age -= 1;
  return age;
}

export function ageCheck(lic: LicenseData, today: IsoDate, minAge = 21): { passed: boolean; idExpired: boolean; reason?: string } {
  if (!lic.dob) return { passed: false, idExpired: false, reason: 'Could not read date of birth' };
  const idExpired = lic.expiry ? lic.expiry < today : false;
  if (idExpired) return { passed: false, idExpired, reason: 'ID is expired' };
  const age = ageOn(lic.dob, today);
  if (age < minAge) return { passed: false, idExpired, reason: `Under ${minAge}` };
  return { passed: true, idExpired };
}
